import { forwardRef, type ReactNode } from "react";
import { Box, Row, Spinner, Text, TextWithTooltip } from "@hightouch/htui";

/** One pickable row in the review popovers: a project, a status or a task to link. */
export const ReviewPickerRow = forwardRef<
  HTMLButtonElement,
  {
    icon?: ReactNode;
    label: string;
    /** A second line under the label, as a linked task shows its project; the label then takes medium weight. */
    description?: string;
    /** Quiet context after the label on its line; it gives way before the label does. */
    meta?: string;
    trailing?: ReactNode;
    isCurrent?: boolean;
    isDisabled: boolean;
    isPending: boolean;
    onClick: () => void;
  }
>(function ReviewPickerRow(
  {
    icon,
    label,
    description,
    meta,
    trailing,
    isCurrent,
    isDisabled,
    isPending,
    onClick,
  },
  ref,
) {
  return (
    <Row
      ref={ref}
      as="button"
      type="button"
      aria-current={isCurrent || undefined}
      disabled={isDisabled}
      align={description ? "start" : "center"}
      gap={2}
      width="100%"
      minHeight={9}
      flexShrink={0}
      px={2}
      py={description ? 2 : 0}
      // Concentric with the popover: its 20px corner less the body's 8px padding.
      borderRadius="2xl"
      textAlign="left"
      color="text.primary"
      _hover={{ bg: "base.background" }}
      _focusVisible={{ outline: "none", boxShadow: "outline" }}
      _disabled={{ cursor: "default", bg: "transparent" }}
      onClick={onClick}
    >
      {icon && (
        <Box
          flexShrink={0}
          display="flex"
          color="icon.secondary"
          // On the title's line when a description sits below it.
          mt={description ? 0.5 : 0}
        >
          {icon}
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        <TextWithTooltip fontWeight={description ? "medium" : undefined}>
          {label}
        </TextWithTooltip>
        {description && (
          <TextWithTooltip size="sm" color="text.secondary">
            {description}
          </TextWithTooltip>
        )}
      </Box>
      {meta && (
        // Capped so the note never squeezes the title it sits beside.
        <Box flexShrink={1} minWidth={0} maxWidth="40%" alignSelf="center">
          <TextWithTooltip size="sm" color="text.tertiary">
            {meta}
          </TextWithTooltip>
        </Box>
      )}
      {(isPending || trailing) && (
        <Box
          flexShrink={0}
          display="flex"
          alignSelf="center"
          color="icon.tertiary"
        >
          {isPending ? <Spinner size="sm" /> : trailing}
        </Box>
      )}
    </Row>
  );
});

/** A muted line in place of rows while they load. */
export function ReviewPickerNote({ children }: { children: string }) {
  return (
    <Box px={2} py={2}>
      <Text color="text.secondary">{children}</Text>
    </Box>
  );
}
